// frontend/src/pages/ProductCompletionOverview.jsx
//
// Strang 1a-6 — die Uebersicht der Produkt-Vollstaendigkeit. NUR LESEND.
//
// Sie beantwortet eine Frage: WELCHE Produkte haben offene Pflicht-Teile, und
// wie viele? Eine Zeile je Produkt, die groessten Luecken zuerst (der Endpunkt
// sortiert `offene_pflicht DESC, nicht_gemessen DESC`).
//
// 🔴 Quelle ist `GET /api/products/completion/summary` — und die liest
//    AUSSCHLIESSLICH die Tabelle `product_completion` (E-U1). Kein Shopify,
//    keine Live-Lesung. Deshalb traegt jede Zeile einen Zeitstempel: der Stand
//    darf veralten, solange er sein Datum zeigt.
//
// 🔴 `completion_pct` kann `null` sein (keine Pflicht-Teile in dieser
//    Kategoriegruppe) — und `null` ist NICHT `0` (Regel 22 Nr. 3). Die Zelle
//    zeigt dann einen Gedankenstrich, nie eine Null.
//
// 🔴 Kein Eingabefeld, kein Speichern. Stufe 1a sieht nur (E-U2).
//
// PR2 PO-Schnellweg (2026-10-27): Filter „New & incomplete (last N days)“ und
// Spalte „Created via“. Der Filter steht in der URL (`?neu=1`), weil
// Dashboard-Kachel und Morgen-Briefing genau dorthin verlinken; der Server liefert
// dann GENAU die Menge ihrer Zahl (`neu=true` ⇒ `neue_unvollstaendige`). „Created
// via“ ist ein Etikett aus `produkt_entstehung` — ohne Zeile „—“, nie ein
// geratener Weg. Seit R16 (27.10.): N kommt aus den Daten (`neu_tage`, W4), eine
// leere Liste ist nur dann eine Entwarnung, wenn nichts ungemessen ist und nichts
// auf den Katalog wartet (`neu`, B1), und der Ausfall des Filters ist benannt,
// mit einem Knopf, der nur diesen Filter ausschaltet (W5, R16 Runde 2 H-b).

import { useCallback, useEffect, useState } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import {
  Box, Button, Card, CardContent, FormControlLabel, Link, Skeleton, Stack, Switch,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
  useMediaQuery, useTheme,
} from '@mui/material'
import PageContainer from '../components/common/PageContainer'
import Panel from '../components/common/Panel'
import ViewHeader from '../components/ViewHeader'
import EmptyState from '../components/common/EmptyState'
import Pill from '../components/common/Pill'
import { api } from '../api/client'
import { FS, SP, HOVER_LIFT_SX, PANEL_SX } from '../theme/tokens'
import { formatBerlinDateTime } from '../utils/time'

/** Der Gedankenstrich fuer „keine Aussage" — dieselbe Hausform wie in `KnShipments`. */
const STRICH = '—'

/** Die Kopfzellen brechen nicht um (Hausform `pages/MarketLab.jsx`). */
const KOPF_SX = { whiteSpace: 'nowrap' }

/**
 * Der prozentuale Anteil — `null` bleibt `null`.
 *
 * 🔴 `completion_pct === 0` und `completion_pct === null` sind VERSCHIEDENE
 * Aussagen: „null von fuenf Pflicht-Teilen" gegen „diese Kategoriegruppe hat
 * keine Pflicht-Teile". Wer beide als `0 %` malt, behauptet eine Luecke, die es
 * nicht gibt (Regel 22 Nr. 3).
 */
function prozentText(pct) {
  if (pct === null || pct === undefined) return STRICH
  return `${pct} %`
}

/** Der Entstehungsweg in Worten. `po` steht nicht hier — er traegt die Bestellung. */
const WEG_TEXT = {
  assistent: 'Wizard',
  ersatzteil: 'Spare part',
  rma: 'RMA',
  sonstig: 'Other',
}

/**
 * „Created via“: die Bestellung als Link, sonst der Weg — und „—“, wenn POG ihn
 * nicht kennt (Produkt vor dem 2026-10-27 oder direkt in Shopify angelegt).
 */
function WegZelle({ weg, poId }) {
  if (weg === 'po') {
    return poId ? (
      <Link component={RouterLink} to={`/purchase_order/${encodeURIComponent(poId)}`} underline="hover">
        {/* R16 W6: `po_id` hat die Form `PO{n:03d}` und traegt das „PO“ schon. */}
        {poId}
      </Link>
    ) : 'Purchase order'
  }
  return WEG_TEXT[weg] || STRICH
}

/** Der Gesamtzustand einer Zeile als Pille — Text traegt die Aussage, nicht die Farbe. */
function ZustandPille({ vollstaendig, offen, nichtGemessen }) {
  if (vollstaendig) return <Pill tone="ok">Complete</Pill>
  if (offen === 0 && nichtGemessen > 0) return <Pill tone="warn">Not measurable</Pill>
  return <Pill tone="err">Open items</Pill>
}

export default function ProductCompletionOverview() {
  const theme = useTheme()
  const istSchmal = useMediaQuery(theme.breakpoints.down('sm'))

  const [nurOffene, setNurOffene] = useState(false)
  // 1b-5: „nur Produkte mit offenen Vorschlaegen“ — der Filter liest die
  // Queue-Tabelle, nicht `product_completion`. Zwei Schalter, zwei Fragen:
  // „fehlt ein Pflichtfeld?“ und „wartet ein Vorschlag auf einen Menschen?“.
  const [nurVorschlaege, setNurVorschlaege] = useState(false)
  // PR2: der Filter „neu“ lebt in der URL — Kachel und Briefing verlinken
  // `?neu=1`. Der Schalter schreibt dorthin zurueck (`replace`, kein neuer
  // Verlaufseintrag je Klick).
  const [suchParameter, setSuchParameter] = useSearchParams()
  const nurNeu = ['1', 'true'].includes(suchParameter.get('neu') || '')
  const setNurNeu = useCallback((an) => {
    setSuchParameter((vorher) => {
      const naechste = new URLSearchParams(vorher)
      if (an) naechste.set('neu', '1')
      else naechste.delete('neu')
      return naechste
    }, { replace: true })
  }, [setSuchParameter])
  const [daten, setDaten] = useState(null)
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState(null)
  // R16 W5: der Maschinenschluessel des Fehlers (`neu_nicht_ermittelbar`) —
  // nur, um ihn BENANNT zu zeigen; er selbst steht nie im Text.
  const [fehlerCode, setFehlerCode] = useState(null)

  const laden = useCallback(() => {
    let abgemeldet = false
    setLaedt(true)
    api.get('/api/products/completion/summary', {
      params: {
        ...(nurOffene ? { vollstaendig: 'false' } : {}),
        ...(nurVorschlaege ? { offene_vorschlaege: 'true' } : {}),
        ...(nurNeu ? { neu: 'true' } : {}),
      },
    })
      .then(({ data }) => {
        if (abgemeldet) return
        setDaten(data)
        setFehler(null)
        setFehlerCode(null)
      })
      // Kein stilles `.catch(() => {})` -- ein Fehler, der die Anzeige
      // bestimmt, muss sichtbar sein.
      .catch((e) => {
        if (abgemeldet) return
        setFehler(e?.message || 'Unknown error')
        setFehlerCode(e?.response?.data?.error || null)
      })
      .finally(() => { if (!abgemeldet) setLaedt(false) })
    return () => { abgemeldet = true }
  }, [nurOffene, nurVorschlaege, nurNeu])

  useEffect(() => laden(), [laden])

  const zeilen = daten?.zeilen || []
  // 🔴 `gesamt` zaehlt die uebersprungene Zeile MIT, `zeilen` nicht: ohne die
  // Subtraktion stuende bei genau einer inkonsistenten Zeile sowohl „Showing the
  // first 49 of 50" als auch „1 product not shown" — zwei Gruende fuer dieselbe
  // fehlende Zeile, und der erste nennt einen, den es nicht gibt (kein Limit
  // greift). Befund R16 W-neu-2.
  // 🔴 EIN Ausdruck fuer das Tor UND den gedruckten Nenner (Befund R15 N2-W1):
  // solange das Tor subtrahiert und der Satz `gesamt` nennt, stehen bei
  // greifendem Limit „Showing the first 5 of 7" und „1 product not shown"
  // untereinander —
  // 5 + 1 = 6, der Nenner zaehlt eine Zeile mit, die nie zeigbar ist (Regel 19
  // §2c: zwei Ziele, eines erledigt). Zweimal derselbe Ausdruck ist genau die
  // Form, in der die beiden auseinanderlaufen; deshalb einmal.
  const zeigbar = daten ? daten.gesamt - (daten.uebersprungen_inkonsistent || 0) : 0
  const abgeschnitten = Boolean(daten) && zeigbar > zeilen.length

  return (
    <PageContainer>
      <ViewHeader
        title="Product completeness"
        subtitle="Which products are still missing master data — read from the stored status, not from Shopify"
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Products', href: '/products' },
          { label: 'Completeness' },
        ]}
      />

      <Stack
        direction="row"
        spacing={SP.card}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: SP.card }}
      >
        <FormControlLabel
          control={(
            <Switch
              checked={nurOffene}
              onChange={(e) => setNurOffene(e.target.checked)}
            />
          )}
          label="Only incomplete"
        />
        <FormControlLabel
          control={(
            <Switch
              checked={nurVorschlaege}
              onChange={(e) => setNurVorschlaege(e.target.checked)}
            />
          )}
          label="Only with open proposals"
        />
        <FormControlLabel
          control={(
            <Switch
              checked={nurNeu}
              onChange={(e) => setNurNeu(e.target.checked)}
            />
          )}
          label={neuSchalterText(daten?.neu_tage)}
        />
      </Stack>

      {/* 🔴 `!laedt` ist PFLICHT, nicht Kosmetik (R16-W1): `laden()` setzt
          `laedt` VOR dem Abruf und raeumt `fehler` erst im `.then`. Ohne die
          Bedingung stehen waehrend eines Wiederholen-Versuchs „could not be
          loaded" UND das Skelett gleichzeitig da — zwei widersprechende
          Aussagen ueber denselben Abruf. Das Panel daneben macht es richtig;
          zwei Flaechen derselben Sache, eine Regel. */}
      {!laedt && fehler && fehlerCode === 'neu_nicht_ermittelbar' && (
        // R16 W5: der Filter „neu“ hat einen eigenen, BENANNTEN Ausfall — und
        // einen Ausweg, der ohne ihn auskommt. R16 Runde 2 H-b: der Knopf schaltet
        // NUR diesen Filter aus und heisst deshalb so. Nicht „Clear all filters“:
        // ausgefallen ist allein die Berechnung von „neu“; „Only incomplete“ und
        // „Only with open proposals“ funktionieren weiter, und wer sie gesetzt
        // hat, soll sie nicht verlieren. „Show all products“ stimmte nicht, sobald
        // einer der beiden an war.
        <EmptyState
          reason="error"
          message="New products could not be determined."
          detail="The “New & incomplete” filter could not be evaluated just now. That does not mean that all new products are complete."
          action={(
            <Stack direction="row" spacing={SP.chip}>
              <Button size="small" onClick={laden}>Retry</Button>
              <Button size="small" onClick={() => setNurNeu(false)}>Turn off “New & incomplete”</Button>
            </Stack>
          )}
        />
      )}
      {!laedt && fehler && fehlerCode !== 'neu_nicht_ermittelbar' && (
        <EmptyState
          reason="error"
          message="Product completeness could not be loaded."
          detail={fehler}
          action={<Button size="small" onClick={laden}>Retry</Button>}
        />
      )}

      {/* 🔴 Skeleton, kein Spinner beim Seitenaufbau (Anti-Pattern 11). Der
          Ladebereich meldet sich als „in Arbeit" (`aria-busy`), die Skelette
          tragen eine testbare Kennung (Regel 17 §2: nicht auf CSS-Klassen). */}
      {laedt && (
        <Panel>
          <Box data-testid="vollstaendigkeit-uebersicht-laden" aria-busy="true">
            <Stack spacing={SP.tight}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton
                  key={i}
                  data-testid="vollstaendigkeit-uebersicht-skelett"
                  variant="rounded"
                  height={44}
                />
              ))}
            </Stack>
          </Box>
        </Panel>
      )}

      {!laedt && !fehler && zeilen.length === 0 && (
        <EmptyState
          reason={nurOffene || nurVorschlaege || nurNeu ? 'no-match' : 'no-data'}
          message={
            [nurOffene, nurVorschlaege, nurNeu].filter(Boolean).length > 1
              ? 'No product matches all selected filters.'
              : nurNeu
                // Positiv NUR, wenn nichts ungemessen ist und nichts auf den
                // Katalog wartet (R16 B1) — `neuLeer` am Dateiende.
                ? neuLeer(daten).message
              : nurVorschlaege
                ? 'No product has open proposals.'
                : nurOffene
                  ? 'No product has open required fields.'
                  : 'No completeness status has been written yet.'
          }
          // 🔴 Nutzerauskunft, kein Implementierungsvokabular (R16-W3): kein
          //    Endpunkt, kein Nachschritt, kein Tabellenname. Was der Nutzer
          //    wissen muss, ist WANN die Angaben entstehen und was es heisst,
          //    wenn hier nichts steht.
          detail={nurNeu && !nurOffene && !nurVorschlaege
            ? neuLeer(daten).detail
            : nurOffene || nurVorschlaege || nurNeu
              ? undefined
              : 'The status is refreshed every night and whenever a product is created. If nothing shows here, that refresh has not produced a result for any product yet.'}
          onClearFilters={nurOffene || nurVorschlaege || nurNeu
            ? () => { setNurOffene(false); setNurVorschlaege(false); setNurNeu(false); }
            : undefined}
        />
      )}

      {!laedt && !fehler && zeilen.length > 0 && (
        <>
          <Stack direction="row" spacing={SP.chip} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: SP.card }}>
            <Typography sx={{ fontSize: FS.sm }}>
              {daten.gesamt} {daten.gesamt === 1 ? 'product' : 'products'}
            </Typography>
            <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
              {daten.offen} with open required fields
            </Typography>
            <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
              {daten.nicht_gemessen} not measurable in total
            </Typography>
            <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
              as of {formatBerlinDateTime(daten.stand)}
            </Typography>
            {/* 🔴 Eine uebersprungene Zeile ist eine AUSSAGE, kein Nichts
                (Regel 17 §1): ohne diese Zeile saehe eine ausgelassene Zeile
                genauso aus wie eine, die es nicht gibt. Sie steht nur da, wenn
                es sie gibt — die Null wird nicht behauptet. */}
            {daten.uebersprungen_inkonsistent > 0 && (
              <Typography sx={{ fontSize: FS.sm, color: 'warning.main' }}>
                {daten.uebersprungen_inkonsistent}{' '}
                {daten.uebersprungen_inkonsistent === 1 ? 'product' : 'products'} not
                shown — the stored status does not add up
              </Typography>
            )}
          </Stack>

          {abgeschnitten && (
            <Typography sx={{ fontSize: FS.sm, color: 'text.secondary', mb: SP.card }}>
              Showing the first {zeilen.length} of {zeigbar} — the largest gaps first.
            </Typography>
          )}

          {!istSchmal && (
            <Panel>
              {/* Hausform wie die Schwesternliste (`KnShipments.jsx`): die
                  Kopfzellen tragen `nowrap` und schieben sonst die SEITE
                  waagerecht. */}
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={KOPF_SX}>Product</TableCell>
                      <TableCell sx={KOPF_SX}>SKU</TableCell>
                      <TableCell sx={KOPF_SX}>Vertical</TableCell>
                      <TableCell sx={KOPF_SX}>Category group</TableCell>
                      <TableCell sx={KOPF_SX}>Created via</TableCell>
                      <TableCell sx={KOPF_SX}>State</TableCell>
                      <TableCell sx={KOPF_SX}>Required</TableCell>
                      <TableCell sx={KOPF_SX}>Missing</TableCell>
                      <TableCell sx={KOPF_SX}>Not measurable</TableCell>
                      <TableCell sx={KOPF_SX}>Completion</TableCell>
                      <TableCell sx={KOPF_SX}>As of</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {zeilen.map((z) => (
                      <TableRow key={z.product_id} hover>
                        <TableCell sx={{ fontSize: FS.sm }}>
                          {z.product_name || STRICH}
                        </TableCell>
                        <TableCell sx={{ fontSize: FS.sm }}>
                          {z.sku ? (
                            <Link component={RouterLink} to={`/products/${encodeURIComponent(z.sku)}`} underline="hover">
                              {z.sku}
                            </Link>
                          ) : STRICH}
                        </TableCell>
                        <TableCell sx={{ fontSize: FS.sm }}>{z.vertical_key || STRICH}</TableCell>
                        <TableCell sx={{ fontSize: FS.sm }}>{z.kategoriegruppe || STRICH}</TableCell>
                        <TableCell sx={{ fontSize: FS.sm, whiteSpace: 'nowrap' }}>
                          <WegZelle weg={z.weg} poId={z.po_id} />
                        </TableCell>
                        <TableCell>
                          <ZustandPille
                            vollstaendig={z.vollstaendig}
                            offen={z.offene_pflicht}
                            nichtGemessen={z.nicht_gemessen}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: FS.sm, whiteSpace: 'nowrap' }}>
                          {z.erfuellte_pflicht} / {z.pflicht_gesamt}
                        </TableCell>
                        <TableCell sx={{ fontSize: FS.sm }}>{z.offene_pflicht}</TableCell>
                        <TableCell sx={{ fontSize: FS.sm }}>{z.nicht_gemessen}</TableCell>
                        <TableCell sx={{ fontSize: FS.sm, whiteSpace: 'nowrap' }}>
                          {prozentText(z.completion_pct)}
                        </TableCell>
                        <TableCell sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
                          {formatBerlinDateTime(z.geprueft_am)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Panel>
          )}

          {istSchmal && (
            <Stack spacing={SP.tight}>
              {zeilen.map((z) => (
                <Card key={z.product_id} sx={{ ...PANEL_SX, ...HOVER_LIFT_SX, p: 0 }}>
                  <CardContent>
                    <Stack direction="row" spacing={SP.chip} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontSize: FS.lg, fontWeight: 600 }}>
                        {z.product_name || z.sku || STRICH}
                      </Typography>
                      <ZustandPille
                        vollstaendig={z.vollstaendig}
                        offen={z.offene_pflicht}
                        nichtGemessen={z.nicht_gemessen}
                      />
                    </Stack>
                    <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
                      {z.sku ? (
                        <Link component={RouterLink} to={`/products/${encodeURIComponent(z.sku)}`} underline="hover">
                          {z.sku}
                        </Link>
                      ) : STRICH}
                      {` · ${z.vertical_key || STRICH} · ${z.kategoriegruppe || STRICH}`}
                    </Typography>
                    <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
                      Created via <WegZelle weg={z.weg} poId={z.po_id} />
                    </Typography>
                    <Typography sx={{ fontSize: FS.sm, mt: SP.chip }}>
                      {z.erfuellte_pflicht} / {z.pflicht_gesamt} required complete
                      {` · ${z.offene_pflicht} missing · ${z.nicht_gemessen} not measurable`}
                      {` · ${prozentText(z.completion_pct)}`}
                    </Typography>
                    <Typography sx={{ fontSize: FS.xs, color: 'text.secondary', mt: SP.chip }}>
                      as of {formatBerlinDateTime(z.geprueft_am)}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </>
      )}
    </PageContainer>
  )
}

/**
 * Das Etikett des Schalters „neu“ (R16 W4). Das Fenster kommt aus den Daten
 * (`neu_tage`, `NEU_TAGE` im Backend), nie als feste Zahl; solange keine Antwort
 * da ist, steht es ohne Zahl. „& incomplete“, weil der Server neu UND
 * unvollstaendig liefert — ein vollstaendiges neues Produkt erscheint nie.
 */
function neuSchalterText(tage) {
  return tage ? `New & incomplete (last ${tage} days)` : 'New & incomplete'
}

/**
 * Der Leerzustand unter dem Filter „neu“ (R16 B1). Eine leere Liste ist nur dann
 * „alles vollstaendig“, wenn nichts ungemessen ist und nichts nur auf den
 * Lieferanten-Katalog wartet — beide Mengen stehen NICHT in der Liste (sie sind
 * nicht in `produkt_ids`), und unbekannt ist nie vollstaendig (Regel 22).
 * Dieselben Mengen wie die Dashboard-Kachel (`nullHinweis` in `UrgencyRow.jsx`),
 * hier aber als GANZER Satz wie die Nachbarzeilen des Leerzustands (R16 Runde 2
 * H-c; die Kachel bleibt ein Bruchstueck), mit Einzahl/Mehrzahl (H-a). Er sagt,
 * warum die Liste leer ist, und behauptet keine Vollstaendigkeit.
 * Ohne `neu`-Block (aelterer Server) eine Aussage nur ueber die Liste.
 */
function neuLeer(daten) {
  const neu = daten?.neu
  const tage = neu?.tage || daten?.neu_tage
  const fenster = tage ? `in the last ${tage} days` : 'recently'
  if (!neu) {
    return { message: `No product created ${fenster} has open required data.`, detail: undefined }
  }
  const teile = []
  const n = neu.nicht_gemessen
  const k = neu.wartet_auf_katalog
  if (n > 0) teile.push(`${n} ${n === 1 ? 'is' : 'are'} not measured yet`)
  if (k > 0) teile.push(`${k} only ${k === 1 ? 'awaits' : 'await'} the vendor catalog`)
  if (!teile.length) {
    // Positiv: die Liste hinter der Dashboard-Kachel ist leer, weil alles
    // fertig ist — keine Stoerung.
    return { message: `No product created ${fenster} is incomplete.`, detail: undefined }
  }
  const saetze = []
  if (neu.nicht_gemessen > 0) {
    saetze.push('A product that has not been measured yet is not known to be complete; its status is written when it is created and every night.')
  }
  if (neu.wartet_auf_katalog > 0) {
    saetze.push('Products awaiting the vendor catalog only miss the vendor article number, which is not counted here.')
  }
  return { message: `Nothing to list for products created ${fenster}: ${teile.join(' and ')}.`, detail: saetze.join(' ') }
}
